// Cloudflare Turnstile widget for the public claim form (bead cca-r11uc).
//
// Draws NOTHING when `siteKey` is empty, so a dev build or a test without
// VITE_TURNSTILE_SITE_KEY renders the page unchanged. With a key it loads
// Cloudflare's script once, draws the widget explicitly, and hands each token to
// `onVerify`. The door (POST /api/reimburse/submit) checks the token on the
// server through @cca/auth/turnstile; this file decides nothing.
//
// A missing token NEVER holds a submit back (cca-8zf3): the page sends whatever
// it has, '' included, and the door decides. So `onVerify('')` is all a failure
// says here: an error, an expiry, a script that will not load, and an unmount
// each clear the token rather than leave a dead one to be sent. A widget that
// is gone can neither refresh nor expire its token (Lazo L2 on the forms twin).
//
// The same shape as cca-forms-web/src/components/Turnstile.tsx and
// cca-parishes-web's TurnstileWidget.tsx. This is the THIRD copy: the forms and
// parishes widgets live in their apps, and an @cca/ui widget does not exist yet
// (ARCH-11; extracting one is its own change).

import { useEffect, useRef, type JSX } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      'unsupported-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
      size?: 'normal' | 'compact' | 'flexible';
      appearance?: 'always' | 'execute' | 'interaction-only';
    },
  ) => string;
  remove: (id: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => {
      // Forget the failure so the next mount tries again, and take the dead
      // tag with it rather than leaving one per attempt in <head>.
      scriptPromise = null;
      s.remove();
      reject(new Error('turnstile_script_failed'));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export interface TurnstileProps {
  /** The public site key. Empty: no widget, no script, nothing rendered. */
  siteKey: string;
  /** The widget's current token, or '' when it has none. */
  onVerify: (token: string) => void;
}

export function Turnstile({ siteKey, onVerify }: TurnstileProps): JSX.Element | null {
  const ref = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);
  // The latest callback, read when the widget calls back, so a fresh function
  // each render does not tear down and redraw the widget.
  const onVerifyRef = useRef(onVerify);
  onVerifyRef.current = onVerify;

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;
    loadScript().then(
      () => {
        if (cancelled || !ref.current || !window.turnstile || widgetId.current) return;
        try {
          widgetId.current = window.turnstile.render(ref.current, {
            sitekey: siteKey,
            callback: (token) => onVerifyRef.current(token),
            'expired-callback': () => onVerifyRef.current(''),
            'error-callback': () => onVerifyRef.current(''),
            'unsupported-callback': () => onVerifyRef.current(''),
            theme: 'light',
            size: 'compact',
            // Invisible for normal visitors; it shows only if a challenge is needed.
            appearance: 'interaction-only',
          });
        } catch {
          // The script loaded but would not draw the widget (a bad site key,
          // say). The form still sends, with no token.
          onVerifyRef.current('');
        }
      },
      () => {
        // The script did not load (a content blocker, a filtered network). The
        // form still sends, with no token.
        if (!cancelled) onVerifyRef.current('');
      },
    );
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* widget already gone */
        }
      }
      widgetId.current = null;
      onVerifyRef.current('');
    };
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={ref} data-turnstile-widget="" style={{ marginTop: 8 }} />;
}
